<script lang="ts">
  import { cn } from '@/lib/utils';
  import type { HTMLButtonAttributes } from 'svelte/elements';
  import type { Snippet } from 'svelte';
  
  type Variant = 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'elevated';
  type Size = 'default' | 'sm' | 'lg' | 'icon';
  
  interface Props extends HTMLButtonAttributes {
    variant?: Variant;
    size?: Size;
    class?: string;
    children?: Snippet;
  }
  
  let {
    variant = 'default',
    size = 'default',
    class: className = '',
    children,
    ...restProps
  }: Props = $props();
  
  const variants: Record<Variant, string> = {
    default:
      'bg-primary text-primary-foreground shadow-soft-sm hover:bg-primary/90 hover:shadow-lifted active:translate-y-[1px]',
    destructive:
      'bg-destructive text-destructive-foreground shadow-soft-sm hover:bg-destructive/90 active:translate-y-[1px]',
    outline:
      'border border-border/70 bg-card text-foreground shadow-soft-sm hover:border-primary/40 hover:bg-primary/5 hover:text-primary active:translate-y-[1px]',
    secondary:
      'bg-secondary text-secondary-foreground shadow-soft-sm hover:bg-secondary/85 hover:shadow-lifted active:translate-y-[1px]',
    ghost: 'hover:bg-primary/10 hover:text-primary active:translate-y-[1px]',
    link: 'text-primary underline-offset-4 hover:underline',
    elevated:
      'bg-card text-foreground shadow-soft-sm border border-transparent hover:shadow-lifted hover:bg-card active:translate-y-[1px]',
  };

  const sizes: Record<Size, string> = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3 text-xs',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  };
</script>

<button
  class={cn(
    'inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 tap-target',
    variants[variant],
    sizes[size],
    className
  )}
  {...restProps}
>
  {#if children}{@render children()}{/if}
</button>